import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { catchError, map, tap } from 'rxjs/operators';
import { API_CONFIG, ApiConfig } from '../api-config';

export interface BranchShippingMethodSelectOption {
  value: string;
  label: string;
}

/**
 * Formas de envío disponibles para una sucursal.
 * GET {apiUrl}/selects/branch-shipping-methods?branchId={id}
 * → { success, data: [{ id, text }] }
 */
@Injectable({ providedIn: 'root' })
export class BranchShippingMethodsSelectService {
  private readonly httpClient = inject(HttpClient);
  private readonly apiConfig = inject<ApiConfig>(API_CONFIG);
  private readonly cache = new Map<string, BranchShippingMethodSelectOption[]>();
  private readonly endpointUrl = '/selects/branch-shipping-methods';

  getOptions(branchId: string): Observable<BranchShippingMethodSelectOption[]> {
    const id = branchId.trim();
    if (!id) return of([]);

    const cached = this.cache.get(id);
    if (cached) return of(cached);

    return this.httpClient
      .get(`${this.apiConfig.apiUrl}${this.endpointUrl}`, {
        params: { branchId: id },
        observe: 'response',
        responseType: 'text',
      })
      .pipe(
        map((resp) => this.parseSelectResponse(resp.body)),
        tap((options) => this.cache.set(id, options)),
        catchError(() => of([])),
      );
  }

  private parseSelectResponse(
    body: unknown,
  ): BranchShippingMethodSelectOption[] {
    if (typeof body !== 'string' || !body) return [];
    try {
      const parsed = JSON.parse(body) as {
        success?: boolean;
        data?: Array<{ id: string; text?: string; label?: string; name?: string }>;
      };
      if (!parsed || parsed.success !== true || !Array.isArray(parsed.data)) {
        return [];
      }
      return parsed.data.map((item) => ({
        value: item.id || '',
        label: item.text || item.label || item.name || '',
      }));
    } catch {
      return [];
    }
  }
}
